import React from 'react';
import type { Column } from '../DataTable';
import type { EntityKind } from '../../utils/entityRoutes';
import { recordLabel, recordNumberLabel } from '../../utils/recordId';
import { useRecordRefs } from '../../utils/recordRefs';
import '../../styles/components/IdColumn.css';

interface IdColumnOptions<T> {
  /** Where the id lives on the row. Defaults to `row.id`. */
  getId?: (row: T) => string | number | null | undefined;
  /** Column key; only needs setting when `id` is already taken on the row type. */
  key?: string;
  header?: string;
}

/** The label itself, for lists that are not a DataTable (cards, custom tables). */
export function RecordIdLabel({ kind, id }: { kind: EntityKind; id: string | number | null | undefined }) {
  // Subscribes this label to the record-number store: it re-renders when the
  // numbers arrive. The tooltip keeps the full id either way.
  useRecordRefs();
  return (
    <span className="rec-id" title={id === null || id === undefined ? undefined : String(id)}>
      {recordLabel(kind, id)}
    </span>
  );
}

/** Plain text for a record id where the surrounding markup is not a RecordIdLabel (a <strong>, a sub-line). */
export function RecordLabelText({ kind, id }: { kind: EntityKind; id: string | number | null | undefined }) {
  useRecordRefs();
  return <>{recordLabel(kind, id)}</>;
}

/**
 * What the ID column sorts on: record number first (zero-padded so the text
 * order is the numeric order), then rows with only a uuid by their id string.
 */
export function idSortKey(kind: EntityKind, id: string | number | null | undefined): string {
  if (id === null || id === undefined || id === '') return '';
  const label = recordNumberLabel(kind, id);
  if (label) return `0${label.slice(label.indexOf('-') + 1).padStart(12, '0')}`;
  return `1${String(id)}`;
}

/**
 * The ID column every list table starts with. Shows the same short label the
 * detail URL is built from (see recordId / entityPath), never wraps, and sorts
 * on the record number when there is one, else the raw id.
 */
export function idColumn<T extends Record<string, any>>(kind: EntityKind, opts: IdColumnOptions<T> = {}): Column<T> {
  const getId = opts.getId ?? ((row: T) => row.id as string | number | null | undefined);
  return {
    key: opts.key ?? 'id',
    header: opts.header ?? 'ID',
    width: '112px',
    sortable: true,
    className: 'rec-id-cell',
    sortValue: row => idSortKey(kind, getId(row)),
    render: (_value, row) => <RecordIdLabel kind={kind} id={getId(row)} />,
  };
}

export default idColumn;
